import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  postcode: string
  behavioural: string
}

const HOW_IT_WORKS: ComparisonRow[] = [
  {
    label: "Targeting method",
    postcode: "Geographic boundaries (postcodes, radius)",
    behavioural: "User actions, interests, life events",
  },
  {
    label: "Data source",
    postcode: "Location data, IP address, GPS",
    behavioural: "Browsing history, app activity, search queries",
  },
  {
    label: "Audience definition",
    postcode: "Everyone within a set area",
    behavioural: "People showing intent signals regardless of location",
  },
  {
    label: "Typical radius",
    postcode: "1-10 miles from a postcode or pin",
    behavioural: "No geographic limit (unless layered with geo)",
  },
  {
    label: "Setup complexity",
    postcode: "Simple: drop a pin, set a radius",
    behavioural: "Moderate: choose interests, behaviours, life events",
  },
  {
    label: "Best use case",
    postcode: "Promoting a specific listing to local buyers",
    behavioural: "Reaching people actively considering a move",
  },
]

const PERFORMANCE_DATA: ComparisonRow[] = [
  {
    label: "Typical reach (per £100 spend)",
    postcode: "5,000-15,000 impressions",
    behavioural: "10,000-30,000 impressions",
  },
  {
    label: "Click-through rate (CTR)",
    postcode: "1.2-2.5% (high local relevance)",
    behavioural: "0.8-1.8% (broader but less local)",
  },
  {
    label: "Cost per lead (CPL)",
    postcode: "£8-20 per enquiry",
    behavioural: "£6-15 per enquiry",
  },
  {
    label: "Lead quality",
    postcode: "High: already local, likely to view",
    behavioural: "Variable: strong intent but may not be local",
  },
  {
    label: "Audience fatigue risk",
    postcode: "High in small areas (same people see it repeatedly)",
    behavioural: "Lower (larger audience pool)",
  },
  {
    label: "Scalability",
    postcode: "Limited by population density",
    behavioural: "Scales well across regions",
  },
]

const PLATFORM_SUPPORT: ComparisonRow[] = [
  {
    label: "Facebook / Meta Ads",
    postcode: "Postcode, radius (1-50 mi), city, county",
    behavioural: "Likely to move, life events, property interests",
  },
  {
    label: "Instagram (via Meta)",
    postcode: "Same as Facebook (shared Ads Manager)",
    behavioural: "Same as Facebook (shared audience tools)",
  },
  {
    label: "Google / YouTube Ads",
    postcode: "Postcode sectors, radius targeting",
    behavioural: "In-market audiences, custom intent, life events",
  },
  {
    label: "TikTok Ads",
    postcode: "City and region only (less granular)",
    behavioural: "Interest categories, hashtag interactions",
  },
  {
    label: "LinkedIn Ads",
    postcode: "City and region only",
    behavioural: "Job changes, company size, seniority",
  },
  {
    label: "Programmatic / display",
    postcode: "Granular postcode-level targeting",
    behavioural: "Third-party data segments, property intenders",
  },
]

const TARGETING_STATS = [
  {
    value: "92%",
    label: "postcode precision",
    detail:
      "Postcode targeting on Meta reaches over 92% of residents within a defined area, making it the most precise local reach available for property ads.",
  },
  {
    value: "3.4x",
    label: "behavioural intent lift",
    detail:
      "Ads served to users flagged as 'likely to move' by Meta generate 3.4 times more enquiries than untargeted campaigns of the same budget.",
  },
  {
    value: "30-50%",
    label: "combined approach savings",
    detail:
      "Layering postcode targeting with behavioural signals reduces cost per lead by 30-50% compared to using either method alone.",
  },
  {
    value: "£6-12",
    label: "average CPL (combined)",
    detail:
      "UK estate agents using a combined geo-plus-behavioural strategy typically achieve a cost per lead between £6 and £12 for property video ads.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "What is postcode targeting in property advertising?",
    a: "Postcode targeting lets you serve ads to people within a specific geographic area, defined by postcodes or a radius around a pin. For property, this means showing a listing ad only to people who live within (or frequently visit) a set distance from the property. It is the simplest way to ensure your ad reaches a relevant local audience.",
  },
  {
    q: "What counts as behavioural targeting for property ads?",
    a: "Behavioural targeting uses signals about what people do online, such as searching for property, visiting estate agent websites, browsing mortgage calculators, or experiencing life events like a new job or marriage. Platforms like Meta and Google track these signals and let you target ads to people showing intent to move, regardless of where they currently live.",
  },
  {
    q: "Which is better for selling a specific listing: postcode or behavioural?",
    a: "For a specific listing, start with postcode targeting to reach local buyers who are most likely to attend a viewing. Then layer in behavioural signals (likely to move, property interests) to filter out people in the area who are not in the market. This combined approach consistently outperforms either method used alone.",
  },
  {
    q: "How small can I make my postcode targeting radius?",
    a: "On Meta (Facebook and Instagram), you can target a radius as small as 1 mile from a specific address or postcode. Google Ads allows similar precision. For most property listings, a 3-5 mile radius is the sweet spot, as it captures the majority of likely local buyers without being so narrow that your audience is too small for the algorithm to optimise effectively.",
  },
  {
    q: "Does behavioural targeting work on TikTok for property?",
    a: "TikTok offers interest-based targeting (home, property, interior design) but lacks the granular behavioural signals available on Meta, such as 'likely to move' or life event triggers. TikTok also has less precise geographic targeting. It is better suited for broad awareness campaigns than for precise targeting of active movers.",
  },
  {
    q: "What budget do I need for combined targeting?",
    a: "A combined geo-plus-behavioural campaign on Meta works well from £150 per month. This is enough to run ads for 5-10 listings with a 3-5 mile radius and behavioural overlays. At this budget, expect 10-25 enquiries per month. Scale up as you see results, and allocate more spend to the combinations that deliver the lowest cost per lead.",
  },
  {
    q: "Can I use postcode targeting for valuation campaigns?",
    a: "Absolutely. Postcode targeting is ideal for valuation (market appraisal) campaigns. Target a specific postcode area with a video ad highlighting recent sold prices and your agency's local expertise. Layer in behavioural signals like 'likely to move' to focus on homeowners who are actively considering selling. This is one of the most cost-effective ways to win new instructions.",
  },
  {
    q: "How do I measure which targeting method works better?",
    a: "Run parallel ad sets with identical creative and budget: one using postcode targeting only, one using behavioural targeting only, and one combining both. After two weeks, compare cost per lead, lead quality (viewings booked), and conversion rate (offers made). Most agents find the combined approach wins, but the data will tell you what works in your specific market.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function TargetingBadge({
  name,
  variant,
}: {
  name: string
  variant: "postcode" | "behavioural"
}) {
  const styles =
    variant === "postcode"
      ? "bg-charcoal text-white"
      : "bg-red text-white"
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${styles}`}
    >
      {name}
    </span>
  )
}

function ComparisonTable({
  rows,
  title,
}: {
  rows: ComparisonRow[]
  title: string
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              {title}
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <TargetingBadge name="Postcode" variant="postcode" />
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <TargetingBadge name="Behavioural" variant="behavioural" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.postcode}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.behavioural}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function PostcodeVsBehaviouralTargeting({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Postcode Targeting vs Behavioural Targeting for Property Video Ads",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            {
              name: "Postcode vs Behavioural Targeting for Property Video Ads",
            },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Targeting strategy guide 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Postcode targeting vs behavioural targeting for property video ads
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Two targeting methods, two very different strengths. This guide
            breaks down postcode (geographic) and behavioural targeting for UK
            estate agents running{" "}
            <Link href="/guides/property-video-marketing" className="text-red underline underline-offset-2 hover:text-red/80">property video ads</Link>{" "}
            on social media, and
            explains why combining both delivers the best results.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <TargetingBadge name="Postcode" variant="postcode" />
            <TargetingBadge name="Behavioural" variant="behavioural" />
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why targeting precision matters
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TARGETING_STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deep-dive cards */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Targeting profiles
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Each method at a glance
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* Postcode */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <TargetingBadge name="Postcode" variant="postcode" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The local precision tool
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Postcode targeting puts your property ad in front of everyone
                  within a defined geographic area. You set a postcode or drop a
                  pin, choose a radius (typically 1-10 miles), and the ad
                  platform shows your video to people in that zone. It is the
                  most straightforward way to reach local buyers for a specific
                  listing.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Hyper-local reach for specific listings
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Simple to set up on every major platform
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      High relevance for viewers (they know the area)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Ideal for market appraisal and valuation campaigns
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Reaches everyone in the area, not just movers
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Small radius means small audience (fatigue risk)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Misses out-of-area buyers relocating in
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Behavioural */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <TargetingBadge name="Behavioural" variant="behavioural" />
                  <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Higher intent
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The intent signal finder
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Behavioural targeting finds people based on what they do, not
                  where they are. Platforms like{" "}
                  <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Meta Ads</a>{" "}
                  and{" "}
                  <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Google Ads</a>{" "}
                  track signals
                  such as property portal visits, mortgage searches, and life
                  events (new job, marriage, growing family). This lets you
                  reach people who are actively considering a move, wherever
                  they happen to be.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reaches people with genuine moving intent
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Captures relocators who are not yet local
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Larger audience pool reduces ad fatigue
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Life event triggers catch movers early in the journey
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Without geo filters, leads may be too far away
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Signal accuracy varies by platform
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      More complex to set up than simple geo targeting
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* How each works table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              How it works
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              How each targeting method works
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Understanding the mechanics helps you choose the right approach
              for each campaign, whether you are promoting a listing, running a
              valuation drive, or building local awareness.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={HOW_IT_WORKS} title="Mechanics" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Performance table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Performance
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Performance and results
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Real-world performance data from UK property video ad campaigns.
              These figures represent typical results for estate agents spending
              £100-500 per month on social media advertising.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={PERFORMANCE_DATA} title="Metric" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Platform support table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Platform support
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Targeting capabilities by platform
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Not every platform offers the same targeting tools. Meta
              (<a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Facebook</a> and Instagram) leads for both methods. Here is how the
              major ad platforms compare.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={PLATFORM_SUPPORT} title="Platform" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Combined targeting strategies */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The winning formula
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Layering geo + behavioural targeting
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              The most effective property ad campaigns use both methods
              together. Postcode targeting sets the geography; behavioural
              signals filter for intent. The result is a smaller, higher-quality
              audience that converts at a lower{" "}
              <Link href="/guides/cost-per-lead-video-ads-vs-traditional-property-marketing" className="text-red underline underline-offset-2 hover:text-red/80">cost per lead</Link>.
            </p>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="mb-3 flex flex-wrap gap-2">
                  <TargetingBadge name="Postcode" variant="postcode" />
                  <span className="inline-flex items-center text-xs font-semibold text-slate">+</span>
                  <TargetingBadge name="Behavioural" variant="behavioural" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Layer 1: Start with geography
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Set your postcode radius to match the listing&rsquo;s natural
                  catchment area. For a city flat, 3-5 miles is typical. For a
                  rural property, 10-15 miles captures the right pool. This
                  ensures every person who sees the ad could realistically
                  attend a viewing.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Layer 2: Add behavioural intent
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Within that geographic area, narrow your audience to people
                  showing moving signals. On Meta, select &ldquo;Likely to
                  move&rdquo; plus property-related interests. On Google, use
                  in-market audiences for real estate. For professional
                  landlords and developers, consider{" "}
                  <a href="https://business.linkedin.com/marketing-solutions/ads" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">LinkedIn Ads</a>{" "}
                  targeting by job title and seniority. This removes the majority
                  of people who live locally but have no intention of moving.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Layer 3: Expand with lookalikes
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Once you have enough data (typically after 50-100 leads),
                  create a lookalike audience from your best converters. Apply
                  the same postcode radius to keep it local. This finds new
                  people who behave like your existing enquirers but have not
                  yet seen your ads. For more on audience strategy, read our{" "}
                  <Link href="/guides/retargeting-vs-cold-audience-property-video-ads" className="text-red underline underline-offset-2 hover:text-red/80">retargeting vs cold audience guide</Link>.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Practical examples */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              In practice
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Targeting strategies for common scenarios
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  New build development
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  New builds attract buyers from a wider area, so extend your
                  postcode radius to 10-15 miles (or target the whole city).
                  Layer with behavioural signals for first-time buyers: age
                  25-35, interests in mortgages and Help to Buy, and the
                  &ldquo;recently engaged&rdquo; life event. On Google, add
                  in-market audiences for &ldquo;residential properties for
                  sale.&rdquo; This combination reaches young couples and
                  professionals actively looking to get on the ladder.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Downsizer targeting
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  For bungalows, retirement properties, or smaller homes suited
                  to downsizers, keep the postcode radius tighter (3-5 miles)
                  as downsizers tend to stay local. Target age 55+ with
                  interests in retirement planning, gardening, and grandchildren.
                  On Meta, the &ldquo;empty nesters&rdquo; and &ldquo;likely to
                  move&rdquo; segments are particularly effective. Video ads
                  that emphasise low maintenance, single-storey living, and
                  proximity to amenities convert well with this audience. Consider combining{" "}
                  <Link href="/guides/paid-video-ads-vs-organic-social-estate-agents" className="text-red underline underline-offset-2 hover:text-red/80">paid and organic social strategies</Link>{" "}
                  for this demographic.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  First-time buyer targeting
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  First-time buyers are the most digitally active segment, so
                  use a broader geographic area (city-wide or even regional)
                  and lean heavily on behavioural signals. Target age 22-35
                  with interests in mortgage calculators, property search apps,
                  and home buying guides. Life events such as new jobs,
                  engagements, and university graduation are strong indicators.
                  Run the campaign on both Meta and{" "}
                  <a href="https://ads.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">TikTok</a>{" "}
                  for maximum reach
                  across this younger demographic. For a full platform comparison, see our{" "}
                  <Link href="/guides/instagram-vs-facebook-vs-tiktok-property-ads" className="text-red underline underline-offset-2 hover:text-red/80">Instagram vs Facebook vs TikTok guide</Link>.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* QuiteRight callout */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-xl border-2 border-red/20 bg-red/5 p-8 md:p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                The creative that makes targeting work
              </p>
              <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
                Great targeting is wasted on poor creative
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                Precision targeting gets your ad in front of the right people.
                But if the creative does not stop the scroll, your budget is
                wasted. QuiteRight creates professional property video ads from
                your listing in under 60 seconds. No filming, no editing. Just
                paste the URL, pick a template, and get a video ready for
                Facebook, Instagram, and TikTok.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate">
                Already using video?{" "}
                <Link href="/sign-up" className="text-red underline underline-offset-2 hover:text-red/80">Get started with QuiteRight</Link>{" "}
                to see the difference professional templates make to your targeting results.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {[
                  {
                    title: "Postcode campaigns",
                    detail:
                      "Create listing videos that show off the property and area to local buyers within your target radius",
                  },
                  {
                    title: "Behavioural campaigns",
                    detail:
                      "Produce eye-catching video ads designed to stop the scroll for people browsing their feed",
                  },
                  {
                    title: "Valuation campaigns",
                    detail:
                      "Generate local market videos with sold prices and area insights to win new instructions",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg bg-white p-4 text-center"
                  >
                    <p className="text-sm font-semibold text-charcoal">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-slate">{item.detail}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/how-it-works"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                title: "Instagram vs Facebook vs TikTok for property ads",
                desc: "Compare all three platforms for property advertising side by side.",
              },
              {
                href: "/guides/social-media-estate-agents",
                title: "Social media for estate agents",
                desc: "A complete guide to building your agency's social media presence.",
              },
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "Why video outperforms photos and how to get started with listing videos.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
              Target precisely. Convert consistently
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
              QuiteRight creates the scroll-stopping property videos that make
              your targeting investment pay off. Professional listing videos in
              under 60 seconds, ready for every platform.{" "}
              <Link href="/how-it-works" className="text-red underline underline-offset-2 hover:text-red/80">See how it works</Link>.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
            <p className="mt-5 text-sm text-warm-grey">
              14-day free trial &middot; No credit card &middot; Cancel any time
            </p>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
